import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import {
  CreditCard,
  Building2,
  FileText,
  Info,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import styles from './EditPaymentReceived.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import Footer from '../../components/Common/Footer/Footer';
import NotFound from '../Error/NotFound';

const EditPaymentReceived = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    project_id: '',
    customer_id: '',
    sales_invoice_id: '',
    invoice_ref: '',
    invoice_date: '',
    due_date: '',
    payment_date: '',
    payment_mode: '2',
    ref_transaction_number: '',
    bank_account_id: '',
    amount: '',
    tds_amount: '',
    remarks: ''
  });

  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    setIsFetching(true);
    try {
      const [projRes, bankRes, paymentRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/bank-accounts-dropdown'),
        api.get(`/customer-payments/${id}`)
      ]);

      setProjects(projRes.data.data || []);
      setBankAccounts((bankRes.data.data || []).map(b => ({ value: b.id, label: b.bank_name })));

      if (paymentRes.data.success) {
        const p = paymentRes.data.data;
        setFormData({
          project_id: p.project_id,
          customer_id: p.project?.customer_id || '',
          sales_invoice_id: p.sales_invoice_id,
          payment_date: p.payment_date,
          payment_mode: String(p.payment_mode),
          ref_transaction_number: p.ref_transaction_number || '',
          bank_account_id: p.bank_account_id,
          amount: p.amount,
          tds_amount: p.tds_amount || '',
          remarks: p.remarks || '',
          invoice_ref: p.sales_invoice?.invoice_number || '',
          invoice_date: p.sales_invoice?.invoice_date || '',
          due_date: p.sales_invoice?.due_date || ''
        });
        
        if (p.project_id) {
          fetchInvoices(p.project_id, p.sales_invoice_id);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load payment details');
    } finally {
      setIsFetching(false);
    }
  };

  if (isFetching) {
    return (
      <div className={styles['edit-payment-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: '20px' }}>
            <Loader2 size={48} className={styles['spinner']} color="#f59e0b" />
            <p style={{ color: '#64748b', fontWeight: 600 }}>Loading payment records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!formData.project_id) {
    return <NotFound />;
  }

  const fetchInvoices = async (projectId, currentInvoiceId = null) => {
    try {
      const res = await api.get('/sales-invoices-dropdown');
      const allInvoices = res.data.data || [];
      const projectInvoices = allInvoices.filter(inv => inv.project_id === projectId);
      const mappedInvoices = projectInvoices.map(inv => ({ 
        value: inv.id, 
        label: inv.invoice_number,
        total_amount: inv.total_amount,
        paid_amount: inv.payments_received_sum_amount || 0,
        due_date: inv.due_date,
        invoice_date: inv.invoice_date
      }));
      setInvoices(mappedInvoices);
      
      if (currentInvoiceId) {
        const inv = mappedInvoices.find(i => i.value === currentInvoiceId);
        setSelectedInvoice(inv);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const handleProjectChange = (option) => {
    const proj = projects.find(p => p.id === option.value);
    setFormData(prev => ({ 
      ...prev, 
      project_id: option.value, 
      customer_id: proj ? proj.customer_id : '',
      sales_invoice_id: '',
      invoice_ref: '',
      invoice_date: '',
      due_date: ''
    }));
    setSelectedInvoice(null);
    if (option.value) {
      fetchInvoices(option.value);
    } else {
      setInvoices([]);
    }
  };

  const handleInvoiceChange = (option) => {
    const inv = invoices.find(i => i.value === option.value);
    setSelectedInvoice(inv);
    setFormData(prev => ({ 
      ...prev, 
      sales_invoice_id: option.value,
      invoice_ref: inv ? inv.label : '',
      invoice_date: inv ? inv.invoice_date : '',
      due_date: inv ? inv.due_date : ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await api.put(`/customer-payments/${id}`, formData);
      if (response.data.success) {
        toast.success('Payment details updated successfully');
        navigate('/payment/payments-received');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error(error.response?.data?.message || 'Failed to update payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const totalAmount = selectedInvoice ? parseFloat(selectedInvoice.total_amount || 0) : 0;
  const paidAmount = selectedInvoice ? parseFloat(selectedInvoice.paid_amount || 0) : 0;
  // If editing, we might need to subtract the current payment from paidAmount to show balance before this payment
  // but usually balance is current.
  const balance = totalAmount - paidAmount;

  return (
    <div className={styles['add-payment-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['header-section']}>
            <h1>Edit Payment Received</h1>
            <p>Update financial transaction details for this record.</p>
            <div style={{ marginTop: '12px' }}>
              <span style={{ 
                background: '#eff6ff', 
                color: '#3b82f6', 
                padding: '6px 14px', 
                borderRadius: '8px', 
                fontSize: '0.85rem', 
                fontWeight: '800' 
              }}>
                Receipt ID: REC-{id}
              </span>
            </div>
          </div>

          {isFetching ? (
            <div className={styles['loading-container']}>
              <Loader2 size={40} className={styles['spinner']} />
              <span style={{ color: '#64748b', fontWeight: 600 }}>Loading payment details...</span>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className={styles['form-layout']}>
                <div className={styles['form-sections']}>
                  <section className={styles['form-card']}>
                    <div className={styles['card-title']}>
                      <Building2 size={20} />
                      <span>Project & Invoice Selection</span>
                    </div>
                    <div className={styles['input-grid']}>
                      <div className={[styles['input-group'], styles['full-width']].join(' ')}>
                        <label>Project</label>
                        <CustomSelect
                          options={projects.map(p => ({ value: p.id, label: p.project_name }))}
                          value={projects.find(p => p.id === formData.project_id) ? { value: formData.project_id, label: projects.find(p => p.id === formData.project_id).project_name } : null}
                          onChange={handleProjectChange}
                          placeholder="Select Project"
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Sales Invoice#</label>
                        <CustomSelect
                          options={invoices}
                          value={invoices.find(i => i.value === formData.sales_invoice_id)}
                          onChange={handleInvoiceChange}
                          placeholder="Select Sales Invoice"
                          isDisabled={!formData.project_id}
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Due Date</label>
                        <input 
                          type="text" 
                          value={formData.due_date || '---'} 
                          disabled 
                          style={{ background: '#f8fafc' }}
                        />
                      </div>
                    </div>
                  </section>

                  <section className={styles['form-card']}>
                    <div className={styles['card-title']}>
                      <CreditCard size={20} />
                      <span>Payment Details</span>
                    </div>
                    <div className={styles['input-grid']}>
                      <div className={styles['input-group']}>
                        <label>Payment Date</label>
                        <CustomDatePicker
                          selected={formData.payment_date}
                          onChange={(date) => setFormData(prev => ({ ...prev, payment_date: date ? date.toISOString().split('T')[0] : '' }))}
                          minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Payment Mode</label>
                        <select name="payment_mode" value={formData.payment_mode} onChange={handleInputChange}>
                          <option value="1">Cash</option>
                          <option value="2">Bank Transfer</option>
                          <option value="3">Cheque</option>
                          <option value="4">Online</option>
                        </select>
                      </div>
                      <div className={styles['input-group']}>
                        <label>Bank Account</label>
                        <CustomSelect
                          options={bankAccounts}
                          value={bankAccounts.find(b => b.value === formData.bank_account_id)}
                          onChange={(option) => setFormData(prev => ({ ...prev, bank_account_id: option.value }))}
                          placeholder="Select Bank Account"
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Ref / Transaction Number</label>
                        <input
                          type="text"
                          name="ref_transaction_number"
                          placeholder="Enter ref #"
                          value={formData.ref_transaction_number}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>Amount Received (₹)</label>
                        <input
                          type="number"
                          name="amount"
                          placeholder="0.00"
                          value={formData.amount}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className={styles['input-group']}>
                        <label>TDS Amount (₹)</label>
                        <input
                          type="number"
                          name="tds_amount"
                          placeholder="0.00"
                          value={formData.tds_amount}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </section>

                  <section className={styles['form-card']}>
                    <div className={styles['card-title']}>
                      <FileText size={20} />
                      <span>Remarks / Notes</span>
                    </div>
                    <div className={[styles['input-group'], styles['full-width']].join(' ')}>
                      <textarea
                        name="remarks"
                        placeholder="Enter any internal notes..."
                        value={formData.remarks}
                        onChange={handleInputChange}
                      />
                    </div>
                  </section>
                </div>

                <div className={styles['summary-sidecar']}>
                  <div className={styles['summary-card']}>
                    <div className={styles['summary-header']}>
                      <h2>Financial Summary</h2>
                    </div>
                    <div className={styles['summary-body']}>
                      <div className={styles['summary-item']}>
                        <span className={styles['label']}>Invoice Amount</span>
                        <span className={styles['value']}>₹ {totalAmount.toLocaleString()}</span>
                      </div>
                      <div className={styles['summary-item']}>
                        <span className={styles['label']}>Total Received</span>
                        <span className={[styles['value'], styles['received']].join(' ')}>₹ {paidAmount.toLocaleString()}</span>
                      </div>

                      <div className={styles['outstanding-box']}>
                        <span className={styles['label']}>Balance Amount</span>
                        <span className={styles['value']}>₹ {balance.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className={styles['summary-footer']}>
                      <Info size={18} />
                      <p>Updating this payment will automatically recalculate the invoice balance.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['header-actions-btns']}>
                <button type="submit" className={styles['btn-primary']} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 size={18} className={styles['spinner']} style={{ color: 'white' }} /> : <CheckCircle2 size={18} />}
                  <span>{isSubmitting ? 'Saving...' : 'Save Changes'}</span>
                </button>
                <button type="button" className={styles['btn-secondary']} onClick={() => navigate('/payment/payments-received')}>Cancel</button>
              </div>
            </form>
          )}
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default EditPaymentReceived;
